import { useGetContent } from '~/hooks/api/useGetContent';
import { useGetAuthToken } from '~/hooks/useGetAuthToken';
import { MEDIA_KIT_EVENTS } from '~/screens/MediaKitPreview/mediaKitAccessEvents';
import { useShareLinkAccessEvent } from '~/screens/SharedSavedList/ShareLinkAccessEventContext';
import { ISearchContentResult } from '~/searchApi';

import { useAddContentStore } from '../../sidebar/useAddContentStore';
import { ContentCard } from './ContentCard/ContentCard';
import { ContentPreviewWrapper } from './ContentPreviewWrapper';
import { getContentLink, getIsStoryExpired } from './utils';

interface SingleContentProps {
  content: ISearchContentResult;
  showInShared: boolean;
}

export const SingleContent = ({
  content,
  showInShared,
}: SingleContentProps) => {
  const authToken = useGetAuthToken({
    creatorSlug: content.creator.slug,
  });

  // Fetch individual content metrics only when showMetrics is true
  const { data: contentResponse } = useGetContent(
    content.id,
    authToken as string,
  );

  const { setIsContentDetailModalOpen } = useAddContentStore();
  // A no-op outside a shared page (the provider's default). A tile carries two
  // distinct actions: following it out to the platform, and opening the in-page
  // detail view (only rendered when the block shows metrics). Only OPENING the
  // detail view is an event — closing it is not a new signal.
  const trackShareLinkAccessEvent = useShareLinkAccessEvent();
  const contentLink = getContentLink(contentResponse?.data.at(0) || content);
  const isStoryExpired = getIsStoryExpired(content);

  return (
    <div className="relative shrink-0 grow snap-start overflow-hidden rounded-8 tab:max-w-[900px] last:tab:max-w-[500px]">
      <ContentPreviewWrapper
        isStoryExpired={isStoryExpired}
        contentLink={contentLink}
        onLinkClick={
          contentLink
            ? () =>
                trackShareLinkAccessEvent(
                  MEDIA_KIT_EVENTS.viewContentAtPlatformClicked,
                  {
                    content_id: content.id,
                    platform: content.platform,
                    creator_id: content.creator.id,
                    destination: contentLink,
                  },
                )
            : undefined
        }
      >
        <ContentCard
          content={content}
          isShared
          showMetrics={showInShared}
          onClick={() => {
            // `id` and `contentId` are two names for the same API field (the
            // search mapper binds `id` to both), so either identifies the piece.
            // `id` is the one foam-web sends for this event — keep them aligned.
            trackShareLinkAccessEvent(
              MEDIA_KIT_EVENTS.viewContentDetailsClicked,
              {
                content_id: content.id,
                platform: content.platform,
                creator_id: content.creator.id,
              },
            );
            setIsContentDetailModalOpen(true, content.contentId);
          }}
        />
      </ContentPreviewWrapper>
    </div>
  );
};
